import styled, { createGlobalStyle, keyframes } from "styled-components";
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { AiOutlineClose } from 'react-icons/ai';
import { FaStore, FaExternalLinkAlt, FaCopy, FaCheck, FaPrint } from 'react-icons/fa';
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

// ============================================================================
// ANIMATIONS
// ============================================================================
const fadeIn = keyframes`
  from {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(12px);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(30px) scale(0.96);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
`;

// ============================================================================
// PRINT STYLES
// ============================================================================
const PrintStyles = createGlobalStyle`
  @page {
    size: A4 portrait;
    margin: 0;
  }

  @media print {
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      height: 100% !important;
      overflow: visible !important;
      background: white !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    body * {
      visibility: hidden;
    }

    .modal-link-cardapio-salao-print-area,
    .modal-link-cardapio-salao-print-area * {
      visibility: visible !important;
    }

    .modal-link-cardapio-salao-print-area {
      position: fixed !important;
      left: 0 !important;
      top: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      max-width: none !important;
      max-height: none !important;
      margin: 0 !important;
      padding: 40px !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      background: white !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      overflow: visible !important;
    }

    /* Hide non-printable elements */
    .crisp-client,
    div[class*="crisp"],
    .leftmenu-mobile-status-toggle,
    #crisp-chatbox,
    [class*="crisp"] {
      display: none !important;
      visibility: hidden !important;
    }
  }
`;

// ============================================================================
// STYLED COMPONENTS
// ============================================================================
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, rgba(139, 69, 19, 0.85), rgba(160, 82, 45, 0.75));
  backdrop-filter: blur(12px);
  animation: ${({ $isOpen }) => $isOpen ? fadeIn : 'none'} 0.35s ease-out;

  @media print {
    background: white;
    backdrop-filter: none;
  }
`;

const ModalCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 24px;
  box-shadow:
    0 32px 64px -12px rgba(139, 69, 19, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #8B4513, #CD853F);
    border-radius: 3px;
  }

  @media print {
    max-width: 100%;
    max-height: none;
    box-shadow: none;
    animation: none;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 28px 24px 20px;
  background: linear-gradient(135deg, #8B4513 0%, #CD853F 100%);
  text-align: center;

  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #8B4513, #CD853F);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(139, 69, 19, 0.4);
    z-index: 1;
  }

  @media print {
    background: white;
    &::after {
      display: none;
    }
  }
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  backdrop-filter: blur(10px);

  svg {
    font-size: 26px;
    color: white;
  }

  @media print {
    background: #8B4513;
    svg {
      color: white;
    }
  }
`;

const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: white;
  letter-spacing: -0.02em;

  @media print {
    color: #1f2937;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);

  @media print {
    color: #6b7280;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);

  svg {
    font-size: 18px;
    color: white;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.08);
  }

  &:active {
    transform: scale(0.96);
  }

  @media print {
    display: none;
  }
`;

const Content = styled.div`
  padding: 36px 24px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const LinkBox = styled.div`
  width: 100%;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(139, 69, 19, 0.08), rgba(205, 133, 63, 0.08));
  border: 1.5px solid rgba(139, 69, 19, 0.2);
  border-radius: 14px;
  text-align: center;
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(139, 69, 19, 0.4);
    background: linear-gradient(135deg, rgba(139, 69, 19, 0.12), rgba(205, 133, 63, 0.12));
  }

  span {
    font-family: 'SF Mono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    font-weight: 500;
    color: #8B4513;
    word-break: break-all;
    line-height: 1.5;
  }

  @media print {
    border: none;
    background: transparent;
    span {
      font-size: 14px;
      color: #1f2937;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;

  @media print {
    display: none;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${({ $variant }) =>
    $variant === 'success'
      ? 'linear-gradient(135deg, #10b981, #059669)'
      : 'linear-gradient(135deg, #8B4513, #CD853F)'};
  color: white;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${({ $variant }) =>
    $variant === 'success'
      ? '0 4px 14px rgba(16, 185, 129, 0.35)'
      : '0 4px 14px rgba(139, 69, 19, 0.35)'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ $variant }) =>
      $variant === 'success'
        ? '0 8px 20px rgba(16, 185, 129, 0.45)'
        : '0 8px 20px rgba(139, 69, 19, 0.45)'};

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 15px;
  }
`;

const QRSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  background: linear-gradient(145deg, #fdf6e3, #f8f4e8);
  border: 2px dashed #d4a574;
  border-radius: 18px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #8B4513;
    animation: ${pulse} 2s ease-in-out infinite;
  }

  @media print {
    border: none;
    background: white;
    animation: none;
  }
`;

const QRWrapper = styled.div`
  padding: 14px;
  background: white;
  border-radius: 14px;
  box-shadow:
    0 4px 20px rgba(139, 69, 19, 0.12),
    0 0 0 1px rgba(139, 69, 19, 0.05);
`;

const QRHint = styled.p`
  margin: 16px 0 0;
  max-width: 240px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  text-align: center;
  line-height: 1.5;
`;

// ============================================================================
// PRINT-ONLY COMPONENTS
// ============================================================================
const PrintOnlySection = styled.div`
  display: none;

  @media print {
    display: flex !important;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 20px;
    text-align: center;
  }
`;

const PrintLogo = styled.div`
  display: none;

  @media print {
    display: flex !important;
    flex-direction: column;
    align-items: center;
    margin-bottom: 30px;

    .print-logo-icon {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #8B4513, #CD853F);
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;

      svg {
        font-size: 40px;
        color: white;
      }
    }

    .print-logo-title {
      font-size: 28px;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }

    .print-logo-subtitle {
      font-size: 16px;
      color: #64748b;
      margin: 8px 0 0;
    }
  }
`;

const PrintQRContainer = styled.div`
  display: none;

  @media print {
    display: flex !important;
    flex-direction: column;
    align-items: center;
    padding: 30px;
    border: 3px solid #8B4513;
    border-radius: 20px;
    background: white;
    margin: 20px 0;

    .print-qr-wrapper {
      padding: 20px;
      background: white;
      border-radius: 16px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }

    .print-scan-text {
      margin-top: 24px;
      font-size: 18px;
      font-weight: 600;
      color: #1e293b;
      text-align: center;
      max-width: 300px;
      line-height: 1.5;
    }
  }
`;

const PrintLink = styled.div`
  display: none;

  @media print {
    display: block !important;
    margin-top: 24px;
    padding: 16px 24px;
    background: #f1f5f9;
    border-radius: 12px;
    font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
    font-size: 12px;
    color: #334155;
    word-break: break-all;
    max-width: 400px;
    text-align: center;
  }
`;

const PrintHint = styled.p`
  display: none;

  @media print {
    display: block !important;
    margin-top: 30px;
    font-size: 12px;
    color: #94a3b8;
    text-align: center;
  }
`;

const ScreenOnly = styled.div`
  @media print {
    display: none !important;
  }
`;

const PrintButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  background: white;
  color: #8B4513;
  font-size: 14px;
  font-weight: 600;
  border: 2px solid #8B4513;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s ease;

  svg {
    font-size: 16px;
  }

  &:hover {
    background: linear-gradient(135deg, #8B4513, #CD853F);
    color: white;
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(139, 69, 19, 0.35);
  }

  &:active {
    transform: translateY(0);
  }

  @media print {
    display: none;
  }
`;

// ============================================================================
// COMPONENT
// ============================================================================
const ModalLinkCardapioSalao = ({ setShowLinkCardapioSalao, showLinkCardapioSalao, nomeEmpresaForUrl }) => {
  const { t } = useTranslation('common');
  const empresa = JSON.parse(localStorage.getItem('empresa'));
  const idEmpresa = empresa?.id_empresa;
  const slug = empresa?.slug;
  const [copied, setCopied] = useState(false);
  const modalRef = useRef(null);

  const hostname = window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : `https://${window.location.hostname}`;
  const yourLink = slug
    ? `${hostname}/c/${slug}?mode=salao`
    : `${hostname}/cardapio-salao/${nomeEmpresaForUrl}/${idEmpresa}`;

  const closeModal = () => setShowLinkCardapioSalao(false);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t('menuLink.copyFailed'));
    }
  };

  const printModal = () => {
    window.print();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const empresaNome = empresa?.name || 'Restaurante';

  return (
    <>
      <PrintStyles />
      <Overlay $isOpen={showLinkCardapioSalao} onClick={handleOverlayClick}>
        <ModalCard ref={modalRef} className="modal-link-cardapio-salao-print-area">
          {/* Screen Content - Hidden when printing */}
          <ScreenOnly>
            <Header>
              <CloseButton onClick={closeModal} aria-label="Close">
                <AiOutlineClose />
              </CloseButton>
              <IconWrapper>
                <FaStore />
              </IconWrapper>
              <Title>{t('menuLink.dineInTitle')}</Title>
              <Subtitle>{t('menuLink.dineInSubtitle')}</Subtitle>
            </Header>

            <Content>
              <LinkBox>
                <span>{yourLink}</span>
              </LinkBox>

              <ButtonGroup>
                <PrimaryButton onClick={() => window.open(yourLink, '_blank')}>
                  <FaExternalLinkAlt />
                  {t('menuLink.openMenu')}
                </PrimaryButton>
                <PrimaryButton
                  $variant={copied ? 'success' : 'default'}
                  onClick={() => copyToClipboard(yourLink)}
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                  {copied ? `✓ ${t('menuLink.copied')}` : t('menuLink.copy')}
                </PrimaryButton>
              </ButtonGroup>

              <QRSection>
                <QRWrapper>
                  <QRCode
                    value={yourLink}
                    size={200}
                    level="H"
                    fgColor="#1e293b"
                  />
                </QRWrapper>
                <QRHint>{t('menuLink.scanQRCode')}</QRHint>
              </QRSection>

              <PrintButton onClick={printModal}>
                <FaPrint />
                {t('menuLink.printQRCode')}
              </PrintButton>
            </Content>
          </ScreenOnly>

          {/* Print-Only Content - Professional layout for printing */}
          <PrintOnlySection>
            <PrintLogo>
              <div className="print-logo-icon">
                <FaStore />
              </div>
              <h1 className="print-logo-title">{empresaNome}</h1>
              <p className="print-logo-subtitle">{t('menuLink.dineInTitle')}</p>
            </PrintLogo>

            <PrintQRContainer>
              <div className="print-qr-wrapper">
                <QRCode
                  value={yourLink}
                  size={250}
                  level="H"
                  fgColor="#1e293b"
                />
              </div>
              <p className="print-scan-text">{t('menuLink.scanQRCode')}</p>
            </PrintQRContainer>

            <PrintLink>{yourLink}</PrintLink>

            <PrintHint>
              {t('menuLink.dineInSubtitle')}
            </PrintHint>
          </PrintOnlySection>
        </ModalCard>
      </Overlay>
    </>
  );
};

export default ModalLinkCardapioSalao;
